import { MAX_INT32_VALUE } from '@tom-ui/utils'
import {
  BlockPositionDto,
  ConfigAreaDto,
  ConfigAreaType,
  ReeferPlugDirection,
  YardBlockDto,
} from '@yard/app/api'
import { StackConfigArea } from '../pages/YardSetup/models/yard-view.model'

export const getYardBlockNameWithAssignedBays = ({
  name,
  assignedBays,
}: {
  id: string
  name: string
  assignedBays: string[]
}): string => {
  if (assignedBays.length === 0) return name

  const min = assignedBays[0]
  const max = assignedBays[assignedBays.length - 1]

  return `${name}.${min}${min !== max ? '-' + max : ''}`
}

export const formatBayName = (bayNames: string[], index: number) => {
  const name = bayNames?.[index]?.trim()
  return name ? name : String(index + 1)
}

export const formatRowName = (rowNames: string[], index: number) => {
  const name = rowNames?.[index]?.trim()
  return name ? name : String(index + 1)
}

export const isSlotInBlockedConfigArea = (
  configAreas: ConfigAreaDto[],
  bayIdx: number,
  rowIdx: number,
) => {
  return configAreas
    .filter(area => area.type === ConfigAreaType.IsBlocked)
    .some(area => {
      return (
        bayIdx >= area.minBay &&
        bayIdx <= area.maxBay &&
        rowIdx >= area.minRow &&
        rowIdx <= area.maxRow
      )
    })
}

export const isSlotBlocked = (configAreas: ConfigAreaDto[], bayIdx: number, rowIdx: number) => {
  return configAreas
    .filter(area => area.type === ConfigAreaType.IsBlocked)
    .some(area => {
      return (
        bayIdx === area.minBay &&
        bayIdx === area.maxBay &&
        rowIdx === area.minRow &&
        rowIdx === area.maxRow
      )
    })
}

export const isConfigAreaBlocked = (
  configAreas: ConfigAreaDto[],
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
) => isConfigAreaOfType(ConfigAreaType.IsBlocked, configAreas, minBay, maxBay, minRow, maxRow)

export const isSlotMaxStackHeight = (
  configAreas: ConfigAreaDto[],
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
) => isConfigAreaOfType(ConfigAreaType.MaxStackHeight, configAreas, minBay, maxBay, minRow, maxRow)

const isConfigAreaOfType = (
  configAreaType: ConfigAreaType,
  configAreas: ConfigAreaDto[],
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
) => {
  return configAreas
    .filter(area => area.type === configAreaType)
    .some(area => {
      return (
        area.minBay === minBay &&
        area.maxBay === maxBay &&
        area.minRow === minRow &&
        area.maxRow === maxRow
      )
    })
}

export const getConfigAreaByType = (
  configAreaType: ConfigAreaType,
  configAreas: ConfigAreaDto[],
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
): ConfigAreaDto | undefined => {
  return configAreas
    .filter(area => area.type === configAreaType)
    .find(area => {
      return (
        area.minBay === minBay &&
        area.maxBay === maxBay &&
        area.minRow === minRow &&
        area.maxRow === maxRow
      )
    })
}

export const getConfigAreaId = (
  configAreaType: ConfigAreaType,
  configAreas: ConfigAreaDto[],
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
  reeferPlugDirection?: ReeferPlugDirection,
): string | undefined => {
  const area = configAreas
    .filter(area => area.type === configAreaType)
    .find(area => {
      return (
        area.minBay === minBay &&
        area.maxBay === maxBay &&
        area.minRow === minRow &&
        area.maxRow === maxRow &&
        (reeferPlugDirection === undefined || area.reeferPlugDirection === reeferPlugDirection)
      )
    })

  return area?.id
}

export const getSlotCountByDestination = (
  yardBlocks: YardBlockDto[],
  blockId: string,
  minBay: number,
  maxBay: number,
  minRow: number,
  maxRow: number,
): number => {
  let count = 0

  const matchingBlock = yardBlocks.find(block => block.id === blockId)
  if (!matchingBlock) return 0

  if (minBay === 0) minBay = 1
  if (minRow === 0) minRow = 1

  if (maxBay === MAX_INT32_VALUE) maxBay = matchingBlock.maxBays
  if (maxRow === MAX_INT32_VALUE) maxRow = matchingBlock.maxRows

  for (let bay = minBay; bay <= maxBay; bay++) {
    for (let row = minRow; row <= maxRow; row++) {
      const isBlocked = isSlotInBlockedConfigArea(matchingBlock.configAreas, bay, row)
      if (isBlocked) continue

      const maxStackHeight = getMaxStackHeightForSlot(matchingBlock, bay, row)
      count += maxStackHeight ?? matchingBlock.maxTiers
    }
  }

  return count
}

export const serializeBlockPositions = (
  yardBlocks: Record<string, YardBlockDto>,
  positions: BlockPositionDto[],
  fallbackBlockId?: string | null,
): string | null => {
  if (positions.length === 0) {
    return fallbackBlockId ? yardBlocks[fallbackBlockId]?.name ?? null : null
  }

  const [first, second] = positions
  const yardBlock = yardBlocks[first.blockId]
  if (!yardBlock) return null

  const bays = [first, second]
    .filter(Boolean)
    .map(p => p.bay)
    .filter(Boolean) as number[]
  const baysNames =
    bays.length > 0 ? bays.map(bay => formatBayName(yardBlock.bayNames, bay - 1)).join('-') : null

  const { row, tier } = first
  const rowName = row ? formatRowName(yardBlock.rowNames, row - 1) : null

  let finalName = yardBlock.name

  if (baysNames) finalName += `.${baysNames}`
  if (rowName) finalName += `.${rowName}`
  if (tier) finalName += `.${tier}`

  return finalName
}

export const getEffectiveMaxTiers = (
  blockMaxTiers: number,
  slotConfig?: StackConfigArea,
): number => {
  const candidates = [
    blockMaxTiers,
    slotConfig?.slotMaxStackHeight,
    slotConfig?.bayMaxStackHeight,
    slotConfig?.rowMaxStackHeight,
  ].filter((v): v is number => v !== undefined)

  return Math.min(...candidates)
}

export const getMaxStackHeightForSlot = (
  block: YardBlockDto,
  bay: number,
  row: number,
): number | undefined => {
  const minBay = 1
  const minRow = 1
  const maxBay = MAX_INT32_VALUE
  const maxRow = MAX_INT32_VALUE

  const candidates = [
    // slot level
    getConfigAreaByType(ConfigAreaType.MaxStackHeight, block.configAreas, bay, bay, row, row),
    // row level
    getConfigAreaByType(ConfigAreaType.MaxStackHeight, block.configAreas, minBay, maxBay, row, row),
    // bay level
    getConfigAreaByType(ConfigAreaType.MaxStackHeight, block.configAreas, bay, bay, minRow, maxRow),
    // block level
    getConfigAreaByType(
      ConfigAreaType.MaxStackHeight,
      block.configAreas,
      minBay,
      maxBay,
      minRow,
      maxRow,
    ),
  ]

  // Currently we take the lowest max stack height defined in the candidates
  // In the future, we may change this logic to take the most specific config area (e.g. slot level has more priority than block level, etc..)
  const heights = candidates
    .filter((area): area is ConfigAreaDto => area != null && area.stackHeight != null)
    .map(area => area.stackHeight!)

  return heights.length > 0 ? Math.min(...heights) : undefined
}
